// TickerTapeModule.tsx
import React from 'react';
import { ModuleBaseModel, ModuleBasePropsModel } from '@/types';

interface TickerTapeMessage {
  text: string;
}

const stylusColorPalette = {
  'Stylus Pale Blue': { bg: '#5798DA', text: '#1B3E4E', activeText: '#FFFFFF' },
  'Stylus Pale Green': { bg: '#73CE98', text: '#305545', activeText: '#FFFFFF' },
  'Stylus Pale Red': { bg: '#EE8B94', text: '#FFFFFF', activeText: '#AE2D2A' },
  'Stylus Amber': { bg: '#E9AD37', text: '#FFFFFF', activeText: '#F3FE88' },
} as const;

const getColorStyles = (colorName: string) => {
  return stylusColorPalette[colorName as keyof typeof stylusColorPalette] || null;
};

type TickerTapeModuleModel = ModuleBaseModel<
  {
    messages: TickerTapeMessage[];
    colourPalette?: string;
  } & ModuleBasePropsModel
>;

const TickerTapeModule: React.FC<TickerTapeModuleModel> = ({ props }) => {
  const { messages = [], colourPalette } = props;
  const colors = typeof colourPalette === 'string' ? getColorStyles(colourPalette) : null;
  const bgColor = colors?.bg;
  const textColor = colors?.text;
  const activeTextColor = colors?.activeText;

  const items = messages.map((msg) => msg.text);
  if (!items.length) return null;

  const repeatedItems = Array(20).fill(items).flat();
  const animationDuration = repeatedItems.length * 3;

  return (
    <div className="w-full overflow-hidden" style={{ backgroundColor: bgColor }}>
      <div className="flex h-14 w-full items-center sm:h-16 md:h-20 lg:h-24">
        <div className="ticker-wrapper">
          <div className="ticker-content">
            {repeatedItems.map((text, idx) => (
              <React.Fragment key={idx}>
                <span className="ticker-item" dangerouslySetInnerHTML={{ __html: text }} />
                <span className="ticker-dot">&bull;</span>
              </React.Fragment>
            ))}
          </div>
          <div className="ticker-content" aria-hidden="true">
            {repeatedItems.map((text, idx) => (
              <React.Fragment key={idx}>
                <span className="ticker-item" dangerouslySetInnerHTML={{ __html: text }} />
                <span className="ticker-dot">&bull;</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      <style jsx>{`
        .ticker-wrapper {
          display: flex;
          width: 100%;
          overflow: hidden;
        }
        .ticker-content {
          display: flex;
          align-items: center;
          animation: scroll ${animationDuration}s linear infinite;
          flex-shrink: 0;
        }
        .ticker-item {
          flex-shrink: 0;
          color: ${textColor};
          font-size: clamp(14px, 3vw, 28px);
          font-weight: 600;
          white-space: nowrap;
          line-height: 1.2;
        }
        .ticker-item :global(a) {
          color: ${activeTextColor};
          text-decoration: none;
          position: relative;
          transition: color 0.3s ease;
        }
        .ticker-item :global(a::after) {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 50%;
          width: 0;
          height: 2px;
          background-color: ${activeTextColor};
          transition:
            width 0.3s ease,
            left 0.3s ease;
        }
        .ticker-item :global(a:hover::after) {
          width: 100%;
          left: 0;
        }
        .ticker-dot {
          flex-shrink: 0;
          color: ${textColor};
          font-size: clamp(14px, 3vw, 28px);
          margin: 0 clamp(15px, 3vw, 30px);
        }
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </div>
  );
};

export default TickerTapeModule;
